import React, { useState } from 'react';
import { Button, Form, Input, message, Modal, Popconfirm, Select, Table, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, TagsOutlined } from '@ant-design/icons';
import { IActivity, IMotif } from '@leedpulse-monorepo/utils';

interface ActivityFormValues {
  id: string;
  label: string;
  description: string;
}

interface MotifFormValues {
  id: string;
  label: string;
  description: string;
  recognition: string;
  default_flow: string;
  activities: string[];
}

interface DomainMotifsCatalogEditorProps {
  activities: IActivity[];
  motifs: IMotif[];
  onActivitiesChange: (activities: IActivity[]) => void;
  onMotifsChange: (motifs: IMotif[]) => void;
  disabled?: boolean;
}

const DomainMotifsCatalogEditor: React.FC<DomainMotifsCatalogEditorProps> = ({
  activities,
  motifs,
  onActivitiesChange,
  onMotifsChange,
  disabled = false,
}) => {
  const [openActivityModal, setOpenActivityModal] = useState(false);
  const [editingActivity, setEditingActivity] = useState<IActivity | null>(null);
  const [activityForm] = Form.useForm<ActivityFormValues>();

  const [openMotifModal, setOpenMotifModal] = useState(false);
  const [editingMotif, setEditingMotif] = useState<IMotif | null>(null);
  const [motifForm] = Form.useForm<MotifFormValues>();

  const handleCloseActivityModal = () => {
    setOpenActivityModal(false);
    setEditingActivity(null);
    activityForm.resetFields();
  };

  const handleCloseMotifModal = () => {
    setOpenMotifModal(false);
    setEditingMotif(null);
    motifForm.resetFields();
  };

  const handleOpenCreateActivity = () => {
    setEditingActivity(null);
    activityForm.resetFields();
    setOpenActivityModal(true);
  };

  const handleOpenEditActivity = (activity: IActivity) => {
    setEditingActivity(activity);
    activityForm.setFieldsValue({
      id: activity.id,
      label: activity.label,
      description: activity.description,
    });
    setOpenActivityModal(true);
  };

  const handleSubmitActivity = async () => {
    const values = await activityForm.validateFields();

    if (editingActivity !== null) {
      onActivitiesChange(
        activities.map((activity) =>
          activity.id === editingActivity.id
            ? { ...activity, label: values.label, description: values.description }
            : activity
        )
      );
    } else {
      onActivitiesChange([...activities, { id: values.id, label: values.label, description: values.description }]);
    }
    handleCloseActivityModal();
  };

  const handleDeleteActivity = (activityId: string) => {
    if (motifs.some((motif) => motif.activities.includes(activityId))) {
      message.error('Cette activité est encore utilisée par un motif : retirez-la du motif avant de la supprimer.');
      return;
    }
    onActivitiesChange(activities.filter((activity) => activity.id !== activityId));
  };

  const handleOpenCreateMotif = () => {
    setEditingMotif(null);
    motifForm.resetFields();
    setOpenMotifModal(true);
  };

  const handleOpenEditMotif = (motif: IMotif) => {
    setEditingMotif(motif);
    motifForm.setFieldsValue({
      id: motif.id,
      label: motif.label,
      description: motif.description,
      recognition: motif.recognition,
      default_flow: motif.default_flow,
      activities: motif.activities,
    });
    setOpenMotifModal(true);
  };

  const handleSubmitMotif = async () => {
    const values = await motifForm.validateFields();

    if (editingMotif !== null) {
      onMotifsChange(
        motifs.map((motif) =>
          motif.id === editingMotif.id
            ? {
              ...motif,
              label: values.label,
              description: values.description,
              recognition: values.recognition,
              default_flow: values.default_flow,
              activities: values.activities ?? [],
            }
            : motif
        )
      );
    } else {
      onMotifsChange([
        ...motifs,
        {
          id: values.id,
          label: values.label,
          description: values.description,
          recognition: values.recognition,
          default_flow: values.default_flow,
          activities: values.activities ?? [],
        },
      ]);
    }
    handleCloseMotifModal();
  };

  const handleDeleteMotif = (motifId: string) => {
    onMotifsChange(motifs.filter((motif) => motif.id !== motifId));
  };

  const activityOptions = activities.map((activity) => ({
    value: activity.id,
    label: activity.label,
  }));

  const activityColumns = [
    { title: 'Id', dataIndex: 'id', key: 'id' },
    { title: 'Label', dataIndex: 'label', key: 'label' },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: IActivity) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title="Modifier">
            <Button size="small" icon={<EditOutlined />} disabled={disabled} onClick={() => handleOpenEditActivity(record)} />
          </Tooltip>
          <Popconfirm
            title="Supprimer cette activité ?"
            description="Cette action est irréversible."
            onConfirm={() => handleDeleteActivity(record.id)}
            okText="Supprimer"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Supprimer">
              <Button size="small" danger disabled={disabled} icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const motifColumns = [
    { title: 'Id', dataIndex: 'id', key: 'id' },
    { title: 'Label', dataIndex: 'label', key: 'label' },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    {
      title: 'Activités',
      dataIndex: 'activities',
      key: 'activities',
      render: (_: unknown, record: IMotif) =>
        record.activities.map((id) => activities.find((a) => a.id === id)?.label ?? id).join(', '),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: IMotif) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title="Modifier">
            <Button size="small" icon={<EditOutlined />} disabled={disabled} onClick={() => handleOpenEditMotif(record)} />
          </Tooltip>
          <Popconfirm
            title="Supprimer ce motif ?"
            description="Cette action est irréversible."
            onConfirm={() => handleDeleteMotif(record.id)}
            okText="Supprimer"
            cancelText="Annuler"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Supprimer">
              <Button size="small" danger disabled={disabled} icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <div className="list-container__controls" style={{ marginBottom: 16 }}>
          <h2>
            <TagsOutlined style={{ marginRight: 6 }} /> Activités
          </h2>
          <Button type="primary" icon={<PlusOutlined />} disabled={disabled} onClick={handleOpenCreateActivity}>
            Nouvelle activité
          </Button>
        </div>
        <Table
          dataSource={activities}
          columns={activityColumns}
          rowKey="id"
          pagination={false}
          size="middle"
          locale={{ emptyText: 'Aucune activité' }}
        />
      </div>

      <div>
        <div className="list-container__controls" style={{ marginBottom: 16 }}>
          <h2>
            <TagsOutlined style={{ marginRight: 6 }} /> Motifs
          </h2>
          <Button type="primary" icon={<PlusOutlined />} disabled={disabled} onClick={handleOpenCreateMotif}>
            Nouveau motif
          </Button>
        </div>
        <Table
          dataSource={motifs}
          columns={motifColumns}
          rowKey="id"
          pagination={false}
          size="middle"
          locale={{ emptyText: 'Aucun motif' }}
        />
      </div>

      <Modal
        title={editingActivity !== null ? 'Modifier l\'activité' : 'Nouvelle activité'}
        open={openActivityModal}
        onOk={handleSubmitActivity}
        onCancel={handleCloseActivityModal}
        okText={editingActivity !== null ? 'Enregistrer' : 'Créer'}
        cancelText="Annuler"
        destroyOnClose
      >
        <Form form={activityForm} layout="vertical" requiredMark={false}>
          <Form.Item
            label="Id"
            name="id"
            rules={[
              { required: true, message: 'L\'id est requis' },
              {
                validator: (_, value) =>
                  editingActivity !== null || !activities.some((activity) => activity.id === value)
                    ? Promise.resolve()
                    : Promise.reject(new Error('Cet id existe déjà')),
              },
            ]}
          >
            <Input placeholder="Ex: vente, location…" disabled={editingActivity !== null} />
          </Form.Item>

          <Form.Item label="Label" name="label" rules={[{ required: true, message: 'Le label est requis' }]}>
            <Input maxLength={100} />
          </Form.Item>

          <Form.Item label="Description" name="description" rules={[{ required: true, message: 'La description est requise' }]}>
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingMotif !== null ? 'Modifier le motif' : 'Nouveau motif'}
        open={openMotifModal}
        onOk={handleSubmitMotif}
        onCancel={handleCloseMotifModal}
        okText={editingMotif !== null ? 'Enregistrer' : 'Créer'}
        cancelText="Annuler"
        destroyOnClose
        width={640}
      >
        <Form form={motifForm} layout="vertical" requiredMark={false}>
          <Form.Item
            label="Id"
            name="id"
            rules={[
              { required: true, message: 'L\'id est requis' },
              {
                validator: (_, value) =>
                  editingMotif !== null || !motifs.some((motif) => motif.id === value)
                    ? Promise.resolve()
                    : Promise.reject(new Error('Cet id existe déjà')),
              },
            ]}
          >
            <Input placeholder="Ex: prix, visite…" disabled={editingMotif !== null} />
          </Form.Item>

          <Form.Item label="Label" name="label" rules={[{ required: true, message: 'Le label est requis' }]}>
            <Input maxLength={100} />
          </Form.Item>

          <Form.Item label="Description" name="description" rules={[{ required: true, message: 'La description est requise' }]}>
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>

          <Form.Item
            label="Activités concernées"
            name="activities"
            tooltip="Laisser vide pour que le motif s'applique quelle que soit l'activité de l'abonné."
          >
            <Select mode="multiple" options={activityOptions} placeholder="Sélectionner les activités" />
          </Form.Item>

          <Form.Item
            label="Reconnaissance"
            name="recognition"
            rules={[{ required: true, message: 'Le texte de reconnaissance est requis' }]}
          >
            <Input.TextArea rows={3} maxLength={1000} showCount />
          </Form.Item>

          <Form.Item
            label="Flux par défaut"
            name="default_flow"
            rules={[{ required: true, message: 'Le flux par défaut est requis' }]}
          >
            <Input.TextArea rows={4} maxLength={2000} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default DomainMotifsCatalogEditor;
